import { create } from 'zustand';

// Mock i18n data for simplicity
const translations = {
  en: {
    welcomeTitle: 'Build Your Skills',
    welcomeDescription: 'Discover courses and workshops to help you grow professionally.',
    searchPlaceholder: 'Search for courses, skills...',
    featuredCourses: 'Featured Courses',
    browseByCategory: 'Browse by Category',
    aiAnalysisTitle: 'Your AI Skill Analysis',
    aiAnalysisDescription: (job) => `To advance from your current role to ${job}, here are the top skills our AI recommends:`,
    networkError: 'A network error occurred.',
    subscriptionStatus: 'Subscription Status',
    upgradeToPremium: 'Upgrade to Premium',
    unlockAllFeatures: 'Unlock all features and courses with Premium.',
    upgradeButton: 'Upgrade Now',
    couldNotAnalyze: 'Could not analyze skills.',
    targetJobDefault: 'a more senior role',
    categoryEngineering: 'Engineering & CAD',
    courseAutoCAD: 'AutoCAD 2D & 3D Mastery',
    courseStructural: 'Structural Analysis with SAP2000',
    internshipOpportunities: 'Internship Opportunities',
    aiMatchDescription: 'Our AI has matched your skills to these opportunities.',
    learningPaths: 'Recommended Learning Paths',
  },
  ar: {
    welcomeTitle: 'Welcome to your bright future',
    welcomeDescription: 'Discover courses and workshops to help you grow professionally.',
    searchPlaceholder: 'Search for courses, skills...',
    featuredCourses: 'Featured Courses',
  browseByCategory: 'Browse by Category',
  aiAnalysisTitle: 'AI Skill Analysis',
  aiAnalysisDescription: (job) => `To advance from your current role to ${job}, here are the top skills our AI recommends:`,
  networkError: 'A network error occurred.',
  subscriptionStatus: 'Subscription Status',
  upgradeToPremium: 'Upgrade to Premium',
  unlockAllFeatures: 'Unlock all features and courses with Premium.',
  upgradeButton: 'Subscribe Now',
  couldNotAnalyze: 'Could not analyze skills.',
  targetJobDefault: 'a more advanced role',
  categoryEngineering: 'Engineering & CAD Design',
  courseAutoCAD: 'Mastering AutoCAD 2D and 3D',
  courseStructural: 'Structural Analysis with SAP2000',
  internshipOpportunities: 'Internship Opportunities',
  aiMatchDescription: 'Our AI has matched your skills with these opportunities.',
  learningPaths: 'Recommended Learning Paths',
  },
};

/**
 * Zustand Store for global state management (Theme, Language, Subscription)
 */
export const useAppStore = create((set) => ({
  // Theme State
  isDarkMode: false,
  toggleDarkMode: () => set((state) => ({ isDarkMode: !state.isDarkMode })),

  // Subscription State (Mock)
  subscription: {
    status: 'Free', // Can be 'Free', 'Premium', 'Trial'
    isPremium: false,
  },
  setSubscriptionStatus: (status) => set((state) => ({
    subscription: {
      status: status,
      isPremium: status === 'Premium',
    }
  })),

  // Language State (i18n)
  language: 'ar', // Default to Arabic
  setLanguage: (lang) => set({ language: lang }),
  
  // Progress Tracking State (Mock for simplicity)
  userProgress: {
    totalCourses: 10,
    completedCourses: 3,
    progressPercentage: 30,
  },
  
  // Translation function
  t: (key, ...args) => {
    const lang = useAppStore.getState().language;
    const text = translations[lang][key];
    if (typeof text === 'function') {
      return text(...args);
    }
    return text || translations['en'][key]; // Fallback to English
  },
  
  // Progress Tracking Actions (Mock)
  completeCourse: (courseId) => set((state) => {
    const newCompleted = state.userProgress.completedCourses + 1;
    const newPercentage = Math.min(100, (newCompleted / state.userProgress.totalCourses) * 100);
    return {
      userProgress: {
        ...state.userProgress,
        completedCourses: newCompleted,
        progressPercentage: newPercentage,
      }
    };
  }),
})); // <-- The missing closing parenthesis was here

// Helper hook for theme colors
export const useThemeColors = () => {
  const isDarkMode = useAppStore((state) => state.isDarkMode);
  
  const colors = {
    primary: '#0e4e96', // Jobeng Blue
    secondary: '#19abb5', // Jobeng Teal
    accent: '#fdb913', // Jobeng Yellow
    background: isDarkMode ? '#121212' : '#F8FAFC',
    card: isDarkMode ? '#1E1E1E' : '#FFFFFF',
    text: isDarkMode ? '#FFFFFF' : '#0F172A',
    textSecondary: isDarkMode ? '#A0A0A0' : '#64748B',
    border: isDarkMode ? '#333333' : '#E2E8F0',
    error: '#EF4444',
  };
  
  return colors;
};